/* Iceberg */
import { Box } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { List } from '@mui/material';

/*  Shared  */
import { Permission } from 'application/Shared/Components/Core';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, Route, SessionHelper } from 'application/Shared/Helpers';
import { TenantProvider } from 'application/Shared/types/tenantProvider';

/* hooks */
import { useFetchTenantProvider } from 'hooks/dataAccess/Tenant/Provider/tenantProvider';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import { Tab } from '../../Components/Tab';
import { IconForSidebarListItem, IconForTab } from './SidebarIcons';
import { SidebarListItem } from './SidebarListItem';

/**
 * Renders the admin navigation links in the sidebar
 * @param {Object} props - Component props
 * @param {boolean} props.applicationLogging - Flag indicating if application logging is enabled
 * @param {string} props.page - Current active page identifier
 * @param {Object} props.session - Session object containing user authentication and permissions data
 * @returns {JSX.Element} A component containing admin navigation links
 */
export const SidebarAdminLinks = ({
    applicationLogging,
    page,
    session
}: {
    applicationLogging: boolean;
    page?: string;
    session: object;
}): JSX.Element => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const {
        providerConfig
    }: { loading: boolean; providerConfig: TenantProvider | null } =
        useFetchTenantProvider(clientId);

    const sessionHelper = new SessionHelper(session);

    // For pages like a client user's 'configureSelfUser' page, this component will be shown since they're
    // not a partner tenant, so we can use the session's tenant ID as a fallback to the clientId.
    const clientIdForLinks = clientId || sessionHelper.getTenantId();

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientIdForLinks,
        partnerId
    });
    const baseReportsRoute = Route.buildBaseReportsRoute({
        clientId: clientIdForLinks,
        partnerId
    });

    const selectUsersMainTab =
        page === 'users' ||
        page === 'user' ||
        page === 'accounts' ||
        page === 'providerUsers' ||
        page === 'providerGroups' ||
        page === 'devices';

    const selectEmployeeLifecycle = page === 'onboard' || page === 'offboard';

    const showUserProvidersLinks =
        Helpers.isGoogleEnabledDeployment(providerConfig) ||
        Helpers.isMicrosoftEntraIDEnabledDeployment(providerConfig);

    const providerType = (providerConfig as TenantProvider | null)
        ?.providerType;

    return (
        <Permission
            required={[
                'system_admin',
                'system_applications',
                'system_users',
                'reporting_organizational',
                'system_integrations',
                'contract_management',
                'system_permissions'
            ]}
            operator="or"
        >
            <List
                aria-labelledby="nested-list-subheader"
                component="nav"
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <Box mt="8px" data-testid="sidebarAdminLinks">
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="Apps" />}
                        name={t('Common.dashboard')}
                        permission="system_admin"
                        route={baseAdminRoute}
                        selected={page === 'dashboard'}
                    />
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="LibraryBook" />}
                        name={t('Common.applications')}
                        permission="system_applications"
                        route={`${baseAdminRoute}/applications/insights`}
                        selected={
                            page === 'applications' || page === 'application'
                        }
                    />
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="TaskList" />}
                        name={t('Common.notes')}
                        permission="system_admin"
                        route={`${baseAdminRoute}/notes`}
                        selected={page === 'notes'}
                    />
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="WarningTriangle" />}
                        name={t('Common.vendor_incidents')}
                        permission="reporting_organizational"
                        route={`${baseAdminRoute}/breaches`}
                        selected={page === 'breaches'}
                    />
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="BarChart" />}
                        name={t('Common.reports')}
                        permission="reporting_organizational"
                        route={`${baseReportsRoute}/reportSelection`}
                        selected={page === 'reports'}
                    />
                    {applicationLogging && (
                        <SidebarListItem
                            icon={<IconForSidebarListItem src="Security" />}
                            name={t('Common.security_logs')}
                            permission="application_logs"
                            route={`${baseAdminRoute}/logs`}
                            selected={page === 'logs'}
                        />
                    )}
                    <Tab
                        icon={<IconForTab src="ProfileWithoutCircle" />}
                        name={t('Users.identitiesAssets')}
                        permission="system_users"
                        route={`${baseAdminRoute}/users`}
                        selected={selectUsersMainTab}
                        subTabs={
                            <>
                                <Tab
                                    hidden={!selectUsersMainTab}
                                    name={t('Common.users')}
                                    permission="system_users"
                                    route={`${baseAdminRoute}/users`}
                                    selected={page === 'users'}
                                    subTab
                                />
                                <Tab
                                    hidden={!selectUsersMainTab}
                                    name={t('Common.accounts')}
                                    permission="system_users"
                                    route={`${baseAdminRoute}/accounts`}
                                    selected={page === 'accounts'}
                                    subTab
                                />
                                {showUserProvidersLinks && (
                                    <>
                                        <Tab
                                            hidden={!selectUsersMainTab}
                                            name={t(
                                                `Provider.${providerType}.usersSideBarTitle`
                                            )}
                                            permission="system_users"
                                            route={`${baseAdminRoute}/providerUsers`}
                                            selected={page === 'providerUsers'}
                                            subTab
                                        />
                                        <Tab
                                            hidden={!selectUsersMainTab}
                                            name={t(
                                                `Provider.${providerType}.groupsSideBarTitle`
                                            )}
                                            permission="system_users"
                                            route={`${baseAdminRoute}/providerGroups`}
                                            selected={page === 'providerGroups'}
                                            subTab
                                        />
                                    </>
                                )}
                                <Tab
                                    hidden={!selectUsersMainTab}
                                    name={t('Common.devices')}
                                    permission="system_users"
                                    route={`${baseAdminRoute}/devices`}
                                    selected={page === 'devices'}
                                    subTab
                                />
                            </>
                        }
                        variant="sidebarMainTab"
                    />

                    <Tab
                        icon={
                            <IconForTab
                                src="FastforwardClock"
                                size={FontSizes[1]}
                            />
                        }
                        name={t('Employee Lifecycle')}
                        permission={['system_admin', 'client_admin']}
                        operator="or"
                        route={`${baseAdminRoute}/saasOps/onboard`}
                        selected={selectEmployeeLifecycle}
                        showNewBadge
                        subTabs={
                            <>
                                <Tab
                                    hidden={!selectEmployeeLifecycle}
                                    name={t('Common.onboard')}
                                    permission="system_users"
                                    route={`${baseAdminRoute}/saasOps/onboard`}
                                    selected={page === 'onboard'}
                                    subTab
                                />
                                <Tab
                                    hidden={!selectEmployeeLifecycle}
                                    name={t('Common.offboard')}
                                    permission="system_users"
                                    route={`${baseAdminRoute}/saasOps/offboard`}
                                    selected={page === 'offboard'}
                                    subTab
                                />
                            </>
                        }
                        variant="sidebarMainTab"
                    />
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="Subscription" />}
                        name={t('Common.contract_management')}
                        permission="contract_management"
                        route={`${baseAdminRoute}/contracts/details`}
                        selected={page === 'contracts'}
                    />
                    <SidebarListItem
                        icon={<IconForSidebarListItem src="Tools" />}
                        name={t('Common.utilities')}
                        permission={[
                            'system_integrations',
                            'system_new_user_popup',
                            'system_permissions',
                            'application_classify'
                        ]}
                        permissionOperator="or"
                        route={`${baseAdminRoute}/configure`}
                        selected={page === 'configure'}
                    />
                    <Tab
                        icon={<IconForTab src="Insights" />}
                        name={t('Common.insights_alerting')}
                        permission="system_admin"
                        route={`${baseAdminRoute}/insights`}
                        selected={page === 'alerts' || page === 'insights'}
                        subTabs={
                            <>
                                <Tab
                                    hidden={
                                        page !== 'insights' && page !== 'alerts'
                                    }
                                    name={t('Common.insights')}
                                    permission="system_admin"
                                    route={`${baseAdminRoute}/insights`}
                                    selected={page === 'insights'}
                                    subTab
                                />
                                <Tab
                                    hidden={
                                        page !== 'insights' && page !== 'alerts'
                                    }
                                    name={t('Common.alerting')}
                                    permission="system_admin"
                                    route={`${baseAdminRoute}/alerts/history`}
                                    selected={page === 'alerts'}
                                    subTab
                                />
                            </>
                        }
                        variant="sidebarMainTab"
                    />
                </Box>
            </List>
        </Permission>
    );
};
